import { useState } from 'react';
import {
  Loader2,
  FileText,
  AlertCircle,
  Plus,
  Archive,
  Clock,
  CheckCircle2,
  Circle,
  ChevronRight,
  Paperclip,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useClientContext } from '../ClientContext';
import type { DocumentRequest } from '@/api/document-requests';
import { documentRequestsApi } from '@/api/document-requests';
import { useClientDocuments, useCreateDocument } from '@/features/legacy/hooks/useDocuments';
import { DocDetailSheet } from '../components/DocDetailSheet';
import { DOCUMENT_REQUEST_ATTACHMENT_ACCEPT } from '../components/DocumentRequestAttachments';

function statusLabel(doc: DocumentRequest): string {
  if (doc.status === 'ARCHIVED') return 'Архив';
  if (doc.status === 'READY_TO_PRINT') return 'К печати';
  if (doc.status === 'IN_PROGRESS') return 'В работе';
  return 'Новая';
}

function DocCard({ doc, onClick }: { doc: DocumentRequest; onClick: () => void }) {
  const isCompleteLike = doc.status === 'READY_TO_PRINT' || doc.status === 'ARCHIVED';
  return (
    <button
      className="w-full p-3 rounded-lg border bg-card border-border text-left hover:bg-muted/50 active:bg-muted transition-colors"
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-1">
        <span className="font-medium text-sm flex-1">{doc.task || doc.comment || 'Заявка'}</span>
        <div className="flex items-center gap-1">
          <Badge
            variant="outline"
            className={
              isCompleteLike
                ? 'text-xs bg-green-50 text-green-700 border-green-300'
                : 'text-xs bg-yellow-50 text-yellow-700 border-yellow-300'
            }
          >
            {isCompleteLike ? (
              <>
                <CheckCircle2 className="w-3 h-3 mr-1" />
                {statusLabel(doc)}
              </>
            ) : (
              <>
                <Circle className="w-3 h-3 mr-1" />
                {statusLabel(doc)}
              </>
            )}
          </Badge>
          <ChevronRight className="w-4 h-4 text-muted-foreground ml-1" />
        </div>
      </div>

      {doc.comment && doc.task && (
        <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{doc.comment}</p>
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {new Date(doc.updatedAt).toLocaleDateString('ru-RU')}
        </span>
        <span>{doc.createdBy?.name || doc.legacyCreatedByName}</span>
      </div>
    </button>
  );
}

export function DocsTab() {
  const { detail, detailLoading } = useClientContext();
  const [showArchive, setShowArchive] = useState(false);
  const [showCreateInput, setShowCreateInput] = useState(false);
  const [newDocTask, setNewDocTask] = useState('');
  const [newDocFiles, setNewDocFiles] = useState<File[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<DocumentRequest | null>(null);

  const clientId = detail?.client.id;

  // Always fetch active documents
  const { data: activeDocs, isLoading: activeLoading, error: activeError } = useClientDocuments(
    clientId,
    0
  );

  // Fetch archived only when showArchive is true
  const { data: archivedDocs, isLoading: archiveLoading } = useClientDocuments(
    showArchive ? clientId : undefined,
    1
  );

  const createDocument = useCreateDocument();

  const handleCreateDocument = () => {
    if (!newDocTask.trim() || !clientId) return;
    createDocument.mutate(
      { clientId, task: newDocTask.trim() },
      {
        onSuccess: async (created) => {
          if (newDocFiles.length > 0) {
            try {
              const result = await documentRequestsApi.uploadAttachments(created.id, newDocFiles, 'MOBILE');
              if (result.uploaded.length > 0) {
                // Toast from useCreateDocument already confirms the request itself.
                toast.success(`Вложений загружено: ${result.uploaded.length}`);
              }
              result.errors.forEach((item) => {
                toast.error(`${item.filename}: ${item.message}`);
              });
            } catch {
              toast.error('Заявка создана, но вложения не загрузились');
            }
          }
          setNewDocTask('');
          setNewDocFiles([]);
          setShowCreateInput(false);
        },
      }
    );
  };

  if (detailLoading) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Загрузка...
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-sm flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Заявки ({activeDocs?.length || 0})
        </h3>
        <div className="flex gap-2">
          <Button
            variant={showArchive ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setShowArchive(!showArchive)}
          >
            <Archive className="h-4 w-4 mr-1" />
            Архив
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCreateInput(!showCreateInput)}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Create document input */}
      {showCreateInput && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input
              placeholder="Описание заявки..."
              value={newDocTask}
              onChange={(e) => setNewDocTask(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateDocument()}
              disabled={createDocument.isPending}
              autoFocus
            />
            <Button
              size="sm"
              onClick={handleCreateDocument}
              disabled={!newDocTask.trim() || !clientId || createDocument.isPending}
            >
              {createDocument.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'OK'
              )}
            </Button>
          </div>
          <label className="flex min-h-10 items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground">
            <Paperclip className="h-4 w-4" />
            <span className="flex-1">
              {newDocFiles.length > 0
                ? `Выбрано файлов: ${newDocFiles.length}`
                : 'Добавить файлы к заявке'}
            </span>
            <input
              type="file"
              multiple
              accept={DOCUMENT_REQUEST_ATTACHMENT_ACCEPT}
              className="hidden"
              onChange={(event) => setNewDocFiles(Array.from(event.target.files || []))}
              disabled={createDocument.isPending}
            />
          </label>
        </div>
      )}

      {/* Loading state */}
      {activeLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      )}

      {/* Error state */}
      {activeError && (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <AlertCircle className="w-8 h-8 text-destructive mb-2" />
          <p className="text-sm text-muted-foreground">Ошибка загрузки</p>
        </div>
      )}

      {/* Active documents */}
      {!activeLoading && !activeError && (
        <div className="space-y-2">
          {(activeDocs?.length || 0) === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              <p className="text-sm">Необработанные заявки не обнаружены</p>
            </div>
          ) : (
            activeDocs?.map((doc) => (
              <DocCard
                key={doc.id}
                doc={doc}
                onClick={() => setSelectedDoc(doc)}
              />
            ))
          )}
        </div>
      )}

      {/* Archived documents */}
      {showArchive && (
        <>
          <div className="flex items-center gap-2 pt-2">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Archive className="h-3 w-3" />
              Архив ({archivedDocs?.length || 0})
            </span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {archiveLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : (archivedDocs?.length || 0) === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              <p className="text-sm">Нет архивных заявок</p>
            </div>
          ) : (
            <div className="space-y-2">
              {archivedDocs?.map((doc) => (
                <DocCard
                  key={doc.id}
                  doc={doc}
                  onClick={() => setSelectedDoc(doc)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Document detail sheet */}
      <DocDetailSheet
        doc={selectedDoc}
        open={!!selectedDoc}
        onOpenChange={(open) => !open && setSelectedDoc(null)}
        onSaved={(updated) => setSelectedDoc(updated)}
      />
    </div>
  );
}
